import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/header.scss";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount, wishlist, removeFromWishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState("");

  const isHome = location.pathname === "/";
  const isCheckout = ["/checkout", "/paysuccess"].includes(location.pathname);
  const savedProducts = productData.filter((product) => wishlist.includes(product.id));

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setWishlistOpen(false);
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const closeWithEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
        setWishlistOpen(false);
        setMoreOpen(false);
      }
    };
    window.addEventListener("keydown", closeWithEscape);
    return () => window.removeEventListener("keydown", closeWithEscape);
  }, []);

  const submitSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/shop?q=${encodeURIComponent(value)}` : "/shop");
  };

  if (isCheckout) {
    return (
      <header className="checkout-site-header">
        <Link className="site-wordmark" to="/" aria-label={`${siteConfig.brandName} home`}>
          <img src={siteConfig.logo} alt={siteConfig.wordmark} />
        </Link>
        <span>Secure checkout</span>
      </header>
    );
  }

  return (
    <>
      <header className={`site-header ${isHome ? "site-header--overlay" : ""}`}>
        <div className="announcement-bar">
          <span>Complimentary delivery from {formatPrice(siteConfig.shipping.freeThreshold)}</span>
          <Link to="/shipping">Delivery & returns</Link>
        </div>

        <div className="site-header__main">
          <div className="site-header__mobile-start">
            <button
              type="button"
              className="icon-button mobile-menu-button"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <FiMenu />
            </button>
          </div>

          <nav className="site-header__nav" aria-label="Primary navigation">
            <NavLink to="/shop">Collection</NavLink>
            {siteConfig.navigation.slice(0, 3).map((item) => (
              <NavLink key={item.label} to={item.to}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Link className="site-wordmark" to="/" aria-label={`${siteConfig.brandName} home`}>
            <img src={siteConfig.logo} alt={siteConfig.wordmark} />
          </Link>

          <div className="site-header__actions">
            <button
              type="button"
              className="icon-button"
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen(true)}
            >
              <FiSearch />
            </button>
            <button
              type="button"
              className="icon-button desktop-heart"
              aria-label={`Wishlist with ${wishlist.length} items`}
              aria-expanded={wishlistOpen}
              onClick={() => setWishlistOpen(true)}
            >
              <FiHeart />
              {wishlist.length > 0 && <span className="action-count">{wishlist.length}</span>}
            </button>
            <Link className="icon-button" to="/cartpage" aria-label={`Shopping bag with ${cartCount} items`}>
              <FiShoppingBag />
              {cartCount > 0 && <span className="action-count">{cartCount}</span>}
            </Link>
          </div>
        </div>

        <nav className="site-header__secondary" aria-label="Shop categories">
          {siteConfig.navigation.map((item) => (
            <Link key={item.label} to={item.to}>
              {item.label}
            </Link>
          ))}
          <Link to="/about">The story</Link>
          <div className="more-menu">
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-controls="more-legal-links"
              onClick={() => setMoreOpen((current) => !current)}
            >
              More
            </button>
            {moreOpen && (
              <div className="more-menu__links" id="more-legal-links">
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/faqs">FAQs</Link>
                <Link to="/terms">Terms & conditions</Link>
              </div>
            )}
          </div>
        </nav>
      </header>

      <div className={`side-panel ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="side-panel__header">
          <span>Menu</span>
          <button type="button" className="icon-button" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
            <FiX />
          </button>
        </div>
        <nav className="mobile-navigation" aria-label="Mobile navigation">
          <Link to="/shop">Shop all</Link>
          {siteConfig.navigation.map((item) => (
            <Link key={item.label} to={item.to}>
              {item.label}
            </Link>
          ))}
          <Link to="/about">Our story</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/faqs">FAQs</Link>
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/cookies">Cookie policy</Link>
          <button type="button" onClick={() => setWishlistOpen(true)}>
            Wishlist ({wishlist.length})
          </button>
        </nav>
      </div>

      <div className={`search-panel ${searchOpen ? "is-open" : ""}`} aria-hidden={!searchOpen}>
        <div className="search-panel__inner">
          <form onSubmit={submitSearch} role="search">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="What are you looking for?"
              aria-label="Search products"
              autoFocus={searchOpen}
            />
            <button type="submit">Search</button>
          </form>
          <button type="button" className="icon-button" aria-label="Close search" onClick={() => setSearchOpen(false)}>
            <FiX />
          </button>
        </div>
        <div className="search-panel__suggestions">
          <span>Popular searches</span>
          <Link to="/shop?category=Shirts">Shirts</Link>
          <Link to="/shop?category=Knitwear">Knitwear</Link>
          <Link to="/shop?tag=summer">The summer edit</Link>
        </div>
      </div>

      <aside className={`wishlist-panel ${wishlistOpen ? "is-open" : ""}`} aria-hidden={!wishlistOpen}>
        <div className="side-panel__header">
          <span>Wishlist ({wishlist.length})</span>
          <button type="button" className="icon-button" aria-label="Close wishlist" onClick={() => setWishlistOpen(false)}>
            <FiX />
          </button>
        </div>
        {savedProducts.length ? (
          <div className="wishlist-panel__items">
            {savedProducts.map((product) => (
              <div className="wishlist-mini" key={product.id}>
                <Link to={`/shopDetails/${product.id}`}>
                  <img src={product.image} alt="" />
                </Link>
                <div>
                  <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                  <span>{formatPrice(product.price)}</span>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${product.name} from wishlist`}
                  onClick={() => removeFromWishlist(product.id)}
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
            <Link className="button button--dark" to="/shop">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="wishlist-panel__empty">
            <FiHeart />
            <p>Your saved pieces will appear here.</p>
            <Link className="text-link" to="/shop">
              Discover the collection
            </Link>
          </div>
        )}
      </aside>

      {(menuOpen || searchOpen || wishlistOpen) && (
        <button
          type="button"
          className="site-overlay"
          aria-label="Close overlay"
          onClick={() => {
            setMenuOpen(false);
            setSearchOpen(false);
            setWishlistOpen(false);
          }}
        />
      )}
    </>
  );
}

export default Header;
